<template lang="pug">
  yz-card.px-6.py-5(color="white")
    .yz-heading-8 {{ $t('panel.summary') }}

    // Row 1: Personal Information
    v-row.mt-5(no-gutters, justify="space-between")
      // Billed to
      v-col
        .yz-heading-9 {{ $t('panel.billed_to') }}

        template(v-if="isBilledToEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.billedTo.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.billedTo.name }}
            yz-badge.ml-1(v-if="summaryData.billedTo.relationship" border, border-color="#0C6DFF", radius="12px" text-color="#0C6DFF" height="16px")
              span.yz-medium-body-2xs {{ summaryData.billedTo.relationship }}

          .yz-regular-body-sm.mt-1(v-if="summaryData.billedTo.employeeId") {{ summaryData.billedTo.employeeId }}

          .d-flex.align-center.mt-1(v-if="summaryData.billedTo.phone")
            .yz-link-body-sm {{ summaryData.billedTo.phone }}
            yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onOpenWhatsAppBilledTo") mdi-whatsapp

      // Patient details
      v-col
        .yz-heading-9 {{ $t('panel.patient_details') }}

        template(v-if="isPatientDetailsEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.patientDetails.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.patientDetails.name }}

          .yz-regular-body-sm.mt-1(v-if="summaryData.patientDetails.id") {{ summaryData.patientDetails.id }}

          .yz-regular-body-sm.mt-1 {{ $t('panel.chit_no') }} :&nbsp;
            span(v-if="summaryData.patientDetails.childNo") {{ summaryData.patientDetails.childNo }}
            span.text-neutral-500(v-else) {{ $t('na') }}

      // Person in charge
      v-col
        .yz-heading-9 {{ $t('panel.person_in_charge') }}

        template(v-if="isPersonInChargeEmpty")
          .yz-regular-body-sm.text-neutral-500.mt-1 {{ $t('na') }}

        template(v-else)
          .d-flex.align-center.mt-1(v-if="summaryData.personInCharge.name")
            yz-icon.mr-1(size="16px" color="yz-neutral-500") $yzi-user-02
            .yz-heading-9.text-neutral-500 {{ summaryData.personInCharge.name }}

          .d-flex.align-center.mt-1(v-if="summaryData.personInCharge.phone")
            .yz-link-body-sm {{ summaryData.personInCharge.phone }}
            yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onOpenWhatsAppPIC") mdi-whatsapp

    // Row 2: Claim Details
    v-row.mt-5(no-gutters)
      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.claim_number') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.claimNumber") {{ summaryData.claimNumber }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.id') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.id") {{ summaryData.id }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.created_date') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.createdDate") {{ summaryData.createdDate }}
          span.text-neutral-500(v-else) {{ $t('na') }}

    // Row 3: Additional Details
    v-row.mt-5(no-gutters)
      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.panel_claim_submission_due_date') }}
        .yz-regular-body-sm.mt-1
          submission-due-date-text(:panel-data="parentClaim.panel")

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.received_date') }}
        .yz-regular-body-sm.mt-1
          span(v-if="summaryData.receivedDate") {{ summaryData.receivedDate }}
          span.text-neutral-500(v-else) {{ $t('na') }}

      v-col(cols="4")
        .yz-heading-9 {{ $t('panel.verification_link') }}
        .d-flex.align-center.mt-1(v-if="summaryData.verificationLink")
          .yz-link-body-sm.truncate {{ summaryData.verificationLink }}
          yz-icon.ml-1.cursor-pointer(size="16px" color="yz-neutral-500" @click="onClickVerificationLink") $yzi-share-03
        .yz-regular-body-sm.text-neutral-500.mt-1(v-else) {{ $t('na') }}

    // Selected Invoices Section
    .d-flex.justify-space-between.align-center.mt-6
      .yz-medium-body-sm {{ $t('panel.selected_invoices') }}
      yz-btn(icon="$yzi-maximize-01" size="sm" outlined background-color="white" @click="showMaximizedDialog = true")

    // Invoices Data Table
    yz-data-table.mt-3(:headers="invoiceHeaders" :items="parentInvoices" :loading="isTableLoading" disable-sort)
      template(v-slot:item.visitDate="{ item }")
        .yz-regular-body-xs.font-medium {{ item.visitDate.date }}
        .yz-regular-body-xs {{ item.visitDate.time }}

      template(v-slot:item.invoiceNo="{ item }")
        .yz-medium-body-sm {{ item.invoiceNo }}

      template(v-slot:item.staffDetails="{ item }")
        .yz-regular-body-xs.font-medium {{ item.staffDetails.name }}
        .d-flex.align-center.mt-1
          .yz-regular-body-xs.mr-1(v-if="item.staffDetails.employeeId") {{ item.staffDetails.employeeId }}
          yz-badge(v-if="item.staffDetails.relationship" border, border-color="#0C6DFF", radius="12px" text-color="#0C6DFF" height="16px")
            span.yz-medium-body-2xs {{ item.staffDetails.relationship }}

      template(v-slot:item.patientDetails="{ item }")
        .py-2
          .yz-regular-body-xs.font-medium {{ item.patientDetails.name }}
          .yz-regular-body-xs {{ item.patientDetails.id }}
          .yz-regular-body-xs {{ $t('panel.chit_no') }} : {{ item.patientDetails.chitNo }}

      template(v-slot:item.mcDays="{ item }")
        .yz-medium-body-sm.text-center {{ item.mcDays }}

      template(v-slot:item.diagnosis="{ item }")
        .yz-regular-body-sm {{ item.diagnosis }}

      template(v-slot:item.treatments="{ item, index }")
        yz-tooltip(direction="bottom" no-arrow, max-width="600px")
          yz-badge(border size="sm") {{ item.treatments }}
          template(#tooltip)
            div.mb-3(v-for="(treatmentItems, categoryName) in item.treatmentDetails" :key="categoryName")
              .yz-heading-9.text-white.mb-1 {{ categoryName }}
              .yz-regular-body-sm.text-white(v-for="treatment in treatmentItems" :key="treatment") {{ treatment }}

      template(v-slot:item.total="{ item }")
        .yz-medium-body-sm.text-right {{ item.total | displayPrice($currency) }}

      template(v-slot:item.actions="{ item }")
        yz-btn(icon="$yzi-trash-03" size="sm" outlined background-color="white" icon-color="danger" color="danger" icon-size="16px" @click="showDeleteConfirmation(item)")

      template(v-slot:footer)
        yz-divider.my-0
        // Pagination and total summary
        .d-flex.justify-space-between.align-center.py-2.px-3
          .yz-regular-body-xs.no-wrap
            | {{ $t('panel.total') }}:
            span.yz-medium-body-xs.ml-1 {{ parentInvoicesTotalCount }} {{ $t('panel.items') }}
          yz-pagination(v-model="page" :page-size="parentInvoicePageSize" :count="parentInvoicesTotalCount" type="item" arrow, @input="onChangePage")

    // Totals
    v-row.mt-5(justify="end", no-gutters)
      v-col(cols="4")
        v-row(no-gutters)
          v-col(cols="8")
            .yz-regular-body-sm {{ $t('panel.invoice_total') }}
          v-col(cols="4")
            .yz-regular-body-sm.text-right {{ invoiceTotal | displayPrice($currency) }}

        yz-divider.my-1

        v-row(no-gutters)
          v-col(cols="8")
            .yz-regular-body-sm {{ $t('panel.claimed_amount') }}
          v-col(cols="4")
            .yz-regular-body-sm.text-right {{ claimedAmount | displayPrice($currency) }}

        // Additional financial fields (shown when status is not 'submitted')
        template(v-if="showAdditionalFinancials")
          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.rejected_amount') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ -rejectedAmount | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('admin_fee') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ -adminFee | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.approved_amount') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right {{ approvedAmount | displayPrice($currency) }}

          yz-divider.my-1

          v-row(no-gutters)
            v-col(cols="8")
              .yz-regular-body-sm {{ $t('panel.total_received') }}
            v-col(cols="4")
              .yz-regular-body-sm.text-right.text-success-500 {{ totalReceived | displayPrice($currency) }}

        yz-divider.mt-1.mb-2

        v-row(no-gutters)
          v-col(cols="8")
            .yz-heading-9 {{ $t('panel.amount_due') }}
          v-col(cols="4")
            .yz-heading-8.text-right {{ amountDue | displayPrice($currency) }}

    // Maximized Bulk Claim Invoices Dialog
    view-maximized-bulk-claim-invoices-dialog(v-if="showMaximizedDialog" v-model="showMaximizedDialog" :parent-claim="parentClaim" :parent-claimed-amount="claimedAmount" :claim-rid="summaryData.claimNumber" @updated="onConfirmRemoveInvoice" @switching-to-single="onSwitchingToSingle")

    // Delete Invoice Bulk Claim Dialog
    invoice-bulk-claim-delete-confirmation-dialog(v-if="showDeleteInvoiceDialog" v-model="showDeleteInvoiceDialog" :parent-uid="invoiceToDelete.uid" @confirmed="onConfirmRemoveInvoice" @canceled="invoiceToDelete = null")
</template>

<script>
import { DateTime } from 'luxon'
import SubmissionDueDateText from '@/components/clinic/panel/claims/SubmissionDueDateText.vue'
import ViewMaximizedBulkClaimInvoicesDialog from '@/components/clinic/panel/dialogs/bulk/ViewMaximizedBulkClaimInvoicesDialog.vue'
import InvoiceBulkClaimDeleteConfirmationDialog from '@/components/clinic/panel/dialogs/bulk/InvoiceBulkClaimDeleteConfirmationDialog.vue'

export default {
  components: { SubmissionDueDateText, ViewMaximizedBulkClaimInvoicesDialog, InvoiceBulkClaimDeleteConfirmationDialog },
  props: {
    parentClaim: {
      type: Object,
      required: true
    },
    parentInvoices: {
      type: Array,
      required: true
    },
    parentInvoicesTotalCount: {
      type: Number,
      required: true
    },
    parentInvoicePageSize: {
      type: Number,
      required: true
    },
    isTableLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Summary data
      summaryData: {
        billedTo: { name: null, employeeId: null, phone: null, relationship: null },
        patientDetails: { name: null, id: null, childNo: null },
        personInCharge: { name: null, phone: null },
        claimNumber: null,
        id: null,
        receivedDate: null,
        createdDate: null,
        verificationLink: null
      },
      // Table configuration
      invoiceHeaders: [
        { text: this.$t('panel.visit_date'), value: 'visitDate', align: 'start', width: '140px' },
        { text: this.$t('panel.invoice_no'), value: 'invoiceNo', align: 'start', width: '105px' },
        { text: this.$t('panel.staff_details'), value: 'staffDetails', align: 'start' },
        { text: this.$t('panel.patient_details'), value: 'patientDetails', align: 'start' },
        { text: this.$t('panel.mc_days'), value: 'mcDays', align: 'center' },
        { text: this.$t('panel.diagnosis'), value: 'diagnosis', align: 'start' },
        { text: this.$t('panel.treatments'), value: 'treatments', align: 'start' },
        { text: this.$t('panel.total'), value: 'total', align: 'end' },
        { text: '', value: 'actions', align: 'end', width: '56px' }
      ],
      // Pagination
      page: 1,
      // Totals
      invoiceTotal: 0,
      claimedAmount: 0,
      rejectedAmount: 0,
      adminFee: 0,
      approvedAmount: 0,
      totalReceived: 0,
      amountDue: 0,
      // Dialog states
      showMaximizedDialog: false,
      showDeleteInvoiceDialog: false,
      invoiceToDelete: null
    }
  },
  computed: {
    isBilledToEmpty() {
      const { name, employeeId, phone } = this.summaryData.billedTo
      return !name && !employeeId && !phone
    },
    isPatientDetailsEmpty() {
      const { name, id, childNo } = this.summaryData.patientDetails
      return !name && !id && !childNo
    },
    isPersonInChargeEmpty() {
      const { name, phone } = this.summaryData.personInCharge
      return !name && !phone
    },
    showAdditionalFinancials() {
      return this.parentClaim?.status !== 'submitted'
    }
  },
  mounted() {
    this.mapData()
  },
  watch: {
    parentClaim: {
      handler() {
        this.mapData()
      },
      deep: true
    }
  },
  methods: {
    mapData() {
      // Return early if props are not available
      if (!this.parentClaim) return

      // Map summary data from parentClaim
      this.summaryData = {
        billedTo: {
          name: null,
          employeeId: null,
          phone: null,
          relationship: null
        },
        patientDetails: {
          name: null,
          id: null,
          chitNo: null
        },
        personInCharge: {
          name: this.parentClaim.panel?.pic_name || null,
          phone: this.parentClaim.panel?.phone || null
        },
        claimNumber: this.parentClaim.rid || null,
        id: this.parentClaim.id || null,
        receivedDate: this.parentClaim.received_date ? this.formatDate(this.parentClaim.received_date) : null,
        createdDate: this.parentClaim.created ? this.formatDate(this.parentClaim.created) : null,
        verificationLink: this.parentClaim.panel?.verification_link || null
      }

      // Map totals
      this.invoiceTotal = this.parentClaim.invoice_total || 0
      this.claimedAmount = this.parentClaim.submitted_amount || 0
      this.rejectedAmount = this.parentClaim.rejected_amount || 0
      this.adminFee = this.parentClaim.admin_fee || 0
      this.approvedAmount = this.parentClaim.approved_amount || 0
      this.totalReceived = this.parentClaim.received_amount || 0
      this.amountDue = this.parentClaim.due_amount || 0
    },
    formatDate(dateString) {
      if (!dateString) return null
      try {
        const date = DateTime.fromISO(dateString)
        return date.toFormat('d MMMM yyyy')
      } catch (error) {
        console.error('Error formatting date:', error)
        return null
      }
    },
    onClickVerificationLink() {
      if (this.summaryData.verificationLink) {
        window.open(this.summaryData.verificationLink, '_blank')
      }
    },
    onOpenWhatsAppBilledTo() {
      this.openWhatsApp(this.summaryData.billedTo.phone)
    },
    onOpenWhatsAppPIC() {
      this.openWhatsApp(this.summaryData.personInCharge.phone)
    },
    openWhatsApp(phoneNumber) {
      if (!phoneNumber) return

      const sanitizedPhone = this.$options.filters.phoneSanitized(phoneNumber)
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${sanitizedPhone}`
      window.open(whatsappUrl, '_blank')
    },
    onChangePage(page) {
      this.page = page
      this.$emit('change-page', page)
    },
    showDeleteConfirmation(item) {
      this.invoiceToDelete = item.originalData
      this.showDeleteInvoiceDialog = true
    },
    onConfirmRemoveInvoice() {
      this.invoiceToDelete = null
      this.$emit('updated')
    },
    onSwitchingToSingle() {
      // Propagate the switching-to-single event from maximized dialog to parent
      this.$emit('switching-to-single')
    }
  }
}
</script>

<style scoped></style>
